import React from "react";
import {
  Box,
  FormControlLabel,
  FormGroup,
  Grid,
  Switch
} from "@material-ui/core";

import TableColumnCheckbox from "./TableColumnCheckbox";
import Section from "../../../elements/Section";
import SectionHeader from "../../../elements/SectionHeader";
import I18nText from "../../../elements/I18nText";
import {
  ButtonsWrapper,
  CancelButton,
  SaveButton
} from "../../../elements/Button";

const MAX_TABLE_COLUMNS = 9;

const Config = React.memo(
  ({
    tableColumns,
    groupByUser,
    onToggleGroupByUser,
    onToggleTableColumn,
    sensorSettings,
    onToggleSensorSetting,
    onSave,
    onClose,
    isSubmitting
  }) => {
    const handleTableColumn = name => event => {
      onToggleTableColumn(name, event.target.checked);
    };

    const handleSensorSetting = name => event => {
      onToggleSensorSetting(name, event.target.checked);
    };

    const selectedCount = Array.isArray(tableColumns) ? tableColumns.length : 0;
    const atMax = selectedCount >= MAX_TABLE_COLUMNS;
    const isChecked = name => tableColumns.indexOf(name) !== -1;
    const isDisabled = name => atMax && !isChecked(name);

    return (
      <>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Section>
              <SectionHeader>
                <I18nText>devices.list.config.sections.general</I18nText>
              </SectionHeader>
              <Grid container>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={groupByUser}
                        onChange={e => onToggleGroupByUser(e.target.checked)}
                        value="grouped"
                        color="primary"
                      />
                    }
                    label={
                      <I18nText>
                        devices.list.config.general.groupByUser
                      </I18nText>
                    }
                  />
                </Grid>
              </Grid>
              <Grid container>
                <SectionHeader>
                  <I18nText>devices.list.config.sections.sensors</I18nText>
                </SectionHeader>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={sensorSettings.showVisuals}
                        onChange={handleSensorSetting("showVisuals")}
                        value="showVisuals"
                        color="primary"
                      />
                    }
                    label={
                      <I18nText>
                        devices.list.config.sensors.showVisuals
                      </I18nText>
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={sensorSettings.showTemperatures}
                        onChange={handleSensorSetting("showTemperatures")}
                        value="showTemperatures"
                        color="primary"
                      />
                    }
                    label={
                      <I18nText>
                        devices.list.config.sensors.showTemperatures
                      </I18nText>
                    }
                  />
                </Grid>
              </Grid>
            </Section>
          </Grid>
          <Grid item xs={12} md={6}>
            <Section>
              <SectionHeader>
                <I18nText>devices.list.config.sections.columns</I18nText>
              </SectionHeader>
              <FormGroup>
                <Grid container>
                  <Grid item xs={6}>
                    <Box>
                      <TableColumnCheckbox
                        name="id"
                        checked={isChecked("id")}
                        onUpdate={handleTableColumn("id")}
                        disabled={isDisabled("id")}
                      />
                    </Box>
                    <Box>
                      <TableColumnCheckbox
                        name="username"
                        checked={isChecked("username")}
                        onUpdate={handleTableColumn("username")}
                        disabled={isDisabled("username")}
                      />
                    </Box>
                    <Box>
                      <TableColumnCheckbox
                        name="name"
                        checked={isChecked("name")}
                        onUpdate={handleTableColumn("name")}
                        disabled={isDisabled("name")}
                      />
                    </Box>

                    <Box>
                      <TableColumnCheckbox
                        name="project"
                        checked={isChecked("project")}
                        onUpdate={handleTableColumn("project")}
                        disabled={isDisabled("project")}
                      />
                    </Box>
                    <Box>
                      <TableColumnCheckbox
                        name="lastReportedAt"
                        checked={isChecked("lastReportedAt")}
                        onUpdate={handleTableColumn("lastReportedAt")}
                        disabled={isDisabled("lastReportedAt")}
                      />
                    </Box>
                    <Box>
                      <TableColumnCheckbox
                        name="status"
                        checked={isChecked("status")}
                        onUpdate={handleTableColumn("status")}
                        disabled={isDisabled("status")}
                      />
                    </Box>
                    <Box>
                      <TableColumnCheckbox
                        name="simCredit"
                        checked={isChecked("simCredit")}
                        onUpdate={handleTableColumn("simCredit")}
                        disabled={isDisabled("simCredit")}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={6}>
                    <Box>
                      <TableColumnCheckbox
                        name="countryCode"
                        checked={isChecked("countryCode")}
                        onUpdate={handleTableColumn("countryCode")}
                        disabled={isDisabled("countryCode")}
                      />
                    </Box>
                    <Box>
                      <TableColumnCheckbox
                        name="postalCode"
                        checked={isChecked("postalCode")}
                        onUpdate={handleTableColumn("postalCode")}
                        disabled={isDisabled("postalCode")}
                      />
                    </Box>
                    <Box>
                      <TableColumnCheckbox
                        name="city"
                        checked={isChecked("city")}
                        onUpdate={handleTableColumn("city")}
                        disabled={isDisabled("city")}
                      />
                    </Box>
                    <Box>
                      <TableColumnCheckbox
                        name="street"
                        checked={isChecked("street")}
                        onUpdate={handleTableColumn("street")}
                        disabled={isDisabled("street")}
                      />
                    </Box>
                  </Grid>
                </Grid>
                {atMax && (
                  <Box mt={1} color="text.secondary" fontSize="0.875rem">
                    <I18nText>devices.list.table.max_note</I18nText>
                  </Box>
                )}
              </FormGroup>
            </Section>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <ButtonsWrapper
              leftButton={
                <CancelButton onClick={onClose} disabled={isSubmitting} />
              }
              rightButton={
                <SaveButton onClick={onSave} disabled={isSubmitting} />
              }
            />
          </Grid>
        </Grid>
      </>
    );
  }
);

export default Config;
